import React, { useState, useEffect, useCallback } from 'react';
import { contactAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Mail, Phone, Calendar, Check, Eye } from 'lucide-react';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState(null);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await contactAPI.getMessages();
      setMessages(data || []);
    } catch (err) { setError('Failed to load messages'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const handleMarkRead = async (id) => {
    try {
      await contactAPI.markRead(id);
      fetchMessages();
    } catch (err) { setError('Failed to mark as read'); }
  };

  return (
    <div data-testid="admin-messages">
      <h1 className="text-2xl font-bold text-white mb-6">Contact Messages</h1>

      {loading ? (
        <div className="text-slate-400">Loading...</div>
      ) : messages.length === 0 ? (
        <div className="text-center text-slate-400 py-12">No messages yet.</div>
      ) : (
        <div className="space-y-3">
          {messages.map(msg => (
            <Card key={msg.id} className={`bg-slate-900 border-slate-800 ${!msg.read ? 'border-l-4 border-l-teal-500' : ''}`} data-testid={`msg-item-${msg.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-white font-medium">{msg.name}</h3>
                      {!msg.read && <Badge className="bg-teal-500/10 text-teal-400 text-xs">New</Badge>}
                    </div>
                    <div className="flex items-center gap-4 text-slate-500 text-sm mb-2">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{msg.email}</span>
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{msg.phone}</span>
                    </div>
                    <div className="cursor-pointer" onClick={() => setExpanded(expanded === msg.id ? null : msg.id)}>
                      <p className={`text-slate-300 text-sm ${expanded === msg.id ? '' : 'line-clamp-2'}`}>
                        {msg.message}
                      </p>
                      {msg.message && msg.message.length > 100 && (
                        <span className="text-teal-400 text-xs mt-1 inline-block">
                          {expanded === msg.id ? 'Show less' : 'Read more'}
                        </span>
                      )}
                    </div>
                  </div>
                  {!msg.read && (
                    <Button size="sm" variant="ghost" onClick={() => handleMarkRead(msg.id)} className="text-slate-400 hover:text-teal-400 ml-3" data-testid={`mark-read-${msg.id}`}>
                      <Check className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
